import React, { useState } from 'react'
import classNames from 'classnames'
import { setCookie } from 'cookies-next'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { ArrowRightIcon } from '@/atoms/Icons/ArrowRightIcon'
import { Input } from '@/atoms/Input'
import { AsLabel, CaptionXS, LabelLG } from '@/atoms/Text'
import { LOGIN_REDIRECT_URL, LOGIN_STARTED_ORIGIN, LOGIN_USER_EMAIL } from '@/constants/cookies'
import { ANGEL_STUDIOS_EMAIL_GROUP_ID } from '@/constants/email'
import { paths } from '@/constants/paths'
import { useThemeContext } from '@/contexts/ThemeContext'
import { AuthApi } from '@/services/Auth/AuthService'
import { useCaptureOrderEmail } from '@/services/EmailCaptureService'
import { isValidEmail } from '@/utils/StripeFormValidator'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { getAttribution } from '@/views/GuildStripeCheckoutSessionView/GuildEmbeddedStripeCheckoutSession'

export type LoginOrigin = 'home' | 'shared' | 'episode' | 'mercy-ships-multi-claim'

interface GuildEmailInputProps {
  isHero?: boolean
  hasUser?: boolean
  isCentered?: boolean
  loginOrigin?: LoginOrigin
  variant?: 'left-white' | 'centered-oxide' | 'centered-white' | 'left-dark'
  sideBySide?: boolean
  isHome?: boolean
  className?: string
  inputContainerClassName?: string
  buttonTextOverride?: string
  returnToUrl?: string
}

export const GuildEmailInput: React.FC<GuildEmailInputProps> = ({
  hasUser = false,
  isHero = true,
  isCentered = true,
  loginOrigin,
  variant = 'centered-oxide',
  sideBySide = false,
  isHome = false,
  className,
  inputContainerClassName,
  buttonTextOverride,
  returnToUrl = getReturnToLoginUrl(loginOrigin),
}) => {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()
  const { isDarkMode } = useThemeContext()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const router = useSafeRouter()
  const captureOrderEmail = useCaptureOrderEmail()
  const { loginUrl, trackLoginStarted } = useLoginUrl(returnToUrl)
  const authService = new AuthApi()

  const buttonText = buttonTextOverride
    ? buttonTextOverride
    : loginOrigin && (loginOrigin === 'shared' || loginOrigin === 'episode')
    ? t('watchFree', 'Watch Free')
    : t('getStarted', 'Get Started')

  const checkIfUserExists = async (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    setCookie(LOGIN_STARTED_ORIGIN, loginOrigin)
    setCookie(LOGIN_REDIRECT_URL, returnToUrl)
    track('Guild Email Input Signup Attempted', { email })
    setError('')
    if (hasUser) {
      router.push(returnToUrl)
      return
    }

    if (!isValidEmail(email)) {
      setError(t('invalidEmail', 'Please enter a valid email address.'))
      return
    }
    event.preventDefault()

    const { projectSlugs } = getAttribution()
    captureOrderEmail({
      position: 'guild-auth-flow',
      subscriptionGroupId: ANGEL_STUDIOS_EMAIL_GROUP_ID,
      email: email,
      projectSlug: projectSlugs ?? '',
      funnel: 'guild',
    })
      .then(() => {
        track('Guild Signup Email Submitted', { email })
      })
      .catch((e) => {
        logger().error('An error occurred while attempting to capture order email', e)
        track('Guild Signup Email Submission Error', { email })
      })

    const userCheck = await fetch(`/api/authByApi/checkUser?email=${encodeURIComponent(email)}`)
    const userCheckJson = await userCheck.json()

    setCookie(LOGIN_USER_EMAIL, email)
    if (userCheckJson.exists) {
      router.push(loginUrl)
      trackLoginStarted('GuildEmailInput')
      return
    } else {
      await authService.sendVerificationCode(email)
      router.push(paths.guild.signup.createAccount)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      checkIfUserExists(event)
    }
  }

  const isLeftWhite = variant === 'left-white'
  const isLeftDark = variant === 'left-dark'
  const isCenteredOxide = variant === 'centered-oxide'
  const isCenteredWhite = variant === 'centered-white'

  return (
    <div onKeyDown={handleKeyDown} className="w-full">
      <When condition={!hasUser}>
        <div
          className={classNames(
            'flex w-full items-center',
            sideBySide ? 'flex-row gap-2' : 'flex-col gap-2 se:-mt-4',
            isHero ? 'lg:flex-row lg:gap-2' : 'lg:gap-8 mt-8 md:mt-0 lg:mt-8',
            {
              'justify-center': isCentered,
              'md:items-start mt-6 se:mt-0 md:mb-6': isLeftWhite,
            },
            className,
          )}
        >
          <div
            className={classNames(
              'lg:w-3/5',
              sideBySide ? 'grow-1' : 'w-full',
              { 'max-w-[326px]': isLeftWhite },
              className,
            )}
          >
            <Input
              label=""
              onChange={(e) => {
                setEmail(e.target.value)
              }}
              value={email}
              placeholder={t('email', 'Email Address')}
              containerClassName={classNames(
                '!mt-0',
                sideBySide ? '' : 'px-4 lg:px-0',
                {
                  'md:px-0': isLeftWhite,
                  '!px-0': isCenteredWhite,
                },
                inputContainerClassName,
              )}
              inputClasses={classNames(
                'photon-paragraph-md min-h-[56px] se:min-h-[48px] border border-b rounded-lg p-2 w-full !duration-[0ms] bg-transparent/35 focus-visible:ring-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent disabled:bg-white',
                isDarkMode && isHero ? 'bg-transparent/35 text-white' : 'bg-white text-black max-w-[450px]',
                {
                  '!min-h-[48px] lg:!min-h-[56px] md:max-w-[326px] !bg-core-gray-900 !text-core-gray-400 border-core-gray-800 border-[1.5px] border-b-[1.5px] placeholder-core-gray-400':
                    isLeftWhite || isCenteredWhite,
                  'max-w-[326px]': isLeftWhite,
                },
              )}
              labelClassName="photon-label-sm font-whitney font-medium"
            />
            {error && (
              <div className="h-4">
                <CaptionXS role="alert" className="mx-auto">
                  <span className="text-error-500">{error}</span>
                </CaptionXS>
              </div>
            )}
          </div>
          <When condition={isLeftWhite}>
            <div
              className={classNames(
                'flex w-full flex-row justify-center md:justify-start lg:mb-0 lg:w-auto',
                loginOrigin === 'episode' ? 'mb-0' : 'mb-12 se:mb-4',
              )}
            >
              <Button
                onClick={checkIfUserExists}
                variant="white"
                className={classNames(
                  'btn btn-standard mb-4 my-0 rounded-lg py-2 min-h-[40px] lg:min-h-[56px] min-w-[142px] lg:px-0',
                  {
                    'lg:w-fit': !isHero,
                    'lg:w-2/5': isHero,
                  },
                  className,
                )}
              >
                <LabelLG
                  as={AsLabel}
                  color="core-gray-950"
                  weight="semibold"
                  className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
                >
                  {buttonText}
                </LabelLG>
              </Button>
              {!isHome && (
                <LinkButton
                  href={paths.guild.join}
                  variant="white"
                  outline
                  className={classNames(
                    'flex justify-center px-3 mb-4 py-[11px] w-full sm:w-auto border-0 hover:!bg-transparent max-w-[115px] max-h-14 min-h-[40px] lg:min-h-[56px] hover:text-core-gray-400',
                    className,
                  )}
                  onClick={() => track('Home Watch Clicked')}
                >
                  {t('learnMore', 'Learn More')}
                </LinkButton>
              )}
            </div>
          </When>
          <When condition={isCenteredOxide}>
            <Button
              onClick={checkIfUserExists}
              variant={isHero ? 'oxide' : 'core-gray-900'}
              className={classNames(
                'btn btn-standard md:mb-0 my-0 rounded-lg py-2 min-h-[56px] se:min-h-[48px] min-w-[142px] !bg-[#FFA41C] !border-[#FFA41C]',
                {
                  'lg:w-fit': !isHero,
                  'lg:w-2/5': isHero,
                  'mb-4': !sideBySide,
                },
                className,
              )}
            >
              <LabelLG
                as={AsLabel}
                color={'black'}
                weight="semibold"
                className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
              >
                {buttonText}
              </LabelLG>
            </Button>
          </When>
          <When condition={isCenteredWhite}>
            <Button
              onClick={checkIfUserExists}
              variant={'white'}
              className={classNames(
                'btn btn-standard md:mb-0 my-0 py-2 min-h-[56px] se:min-h-[48px] min-w-[142px] w-full mx-4 lg:mx-0 md:w-fit mt-2 lg:mt-0 rounded-lg',
                {
                  'lg:w-fit': !isHero,
                  'lg:w-2/5': isHero,
                  'mb-4': !sideBySide,
                },
                className,
              )}
            >
              <LabelLG
                as={AsLabel}
                color={'black'}
                weight="semibold"
                className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-2"
              >
                {buttonText}
              </LabelLG>
              <ArrowRightIcon className="ml-2" size={24} color="black" />
            </Button>
          </When>
          <When condition={isLeftDark}>
            <Button
              onClick={checkIfUserExists}
              variant="black"
              className={classNames(
                'btn btn-standard md:mb-0 my-0 py-2 min-h-[56px] se:min-h-[48px] min-w-[142px] w-full mx-4 lg:mx-0 md:w-fit mt-2 lg:mt-0 rounded-lg',
                {
                  'lg:w-fit': !isHero,
                  'lg:w-2/5': isHero,
                  'mb-4': !sideBySide,
                },
                className,
              )}
            >
              <LabelLG
                as={AsLabel}
                color={'white'}
                weight="semibold"
                className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-2"
              >
                {buttonText}
              </LabelLG>
            </Button>
          </When>
        </div>
      </When>
      <When condition={hasUser}>
        <div
          className={classNames(
            'flex w-full flex-col gap-8 se:-mt-4 se:gap-6 items-center',
            isHero ? 'lg:flex-row lg:gap-2' : 'lg:gap-8 mt-8',
            {
              'justify-center': isCentered,
            },
          )}
        >
          <Button
            onClick={checkIfUserExists}
            variant={isHero ? 'oxide' : 'core-gray-900'}
            className={classNames(
              'btn btn-standard mb-4 my-0 lg:w-2/5 rounded-lg py-3 min-h-[56px] !bg-[#FFA41C] !border-[#FFA41C]',
              {
                'lg:w-fit': !isHero,
                'lg:w-2/5': isHero,
                'lg:mx-auto': isCentered,
              },
              className,
            )}
          >
            <LabelLG
              as={AsLabel}
              color={'black'}
              weight="semibold"
              className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
            >
              {buttonText}
            </LabelLG>
          </Button>
        </div>
      </When>
    </div>
  )
}

function getReturnToLoginUrl(loginOrigin?: LoginOrigin) {
  if (loginOrigin === 'home') {
    return paths.guild.pricing
  } else if (loginOrigin === 'mercy-ships-multi-claim') {
    return '/m/shared/mercy-ships'
  } else if (loginOrigin === 'episode') {
    return typeof window !== 'undefined' ? window.location.href : paths.watch.index
  } else {
    return paths.guild.pricing
  }
}
